import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { calcMonthly } from "@/lib/providers/index";
import { Check, Calendar, Users, MapPin, CreditCard } from "lucide-react";

interface PaymentConfirmationProps {
  bookingData: {
    packageId: string;
    startDate: string;
    endDate: string;
    travelers: number;
    rooms: number;
    selectedProvider?: string;
    specialRequests?: string;
  };
  onConfirm: () => void;
}

export default function PaymentConfirmation({ bookingData, onConfirm }: PaymentConfirmationProps) {
  const packagePrice = 4200; // Mock price
  const monthlyPayment = calcMonthly(packagePrice, 12);
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateNights = () => {
    const start = new Date(bookingData.startDate);
    const end = new Date(bookingData.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const providerName = bookingData.selectedProvider === "tabby" ? "Tabby" : 
                      bookingData.selectedProvider === "tamara" ? "Tamara" : 
                      bookingData.selectedProvider === "cashew" ? "Cashew" : "BNPL Provider";

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-foreground">Confirm Your Booking</h2>
      <p className="text-muted-foreground mb-8">
        Please review your booking details before proceeding to payment.
      </p>

      <div className="space-y-6 mb-8">
        {/* Package Summary */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Package Details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Package ID</span>
              <span className="text-foreground font-medium">{bookingData.packageId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Destination</span>
              <span className="text-foreground font-medium">Selected Package</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration</span>
              <span className="text-foreground font-medium">{calculateNights()} nights</span>
            </div>
          </div>
        </Card>

        {/* Travel Details */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Travel Details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Departure</span>
              <span className="text-foreground font-medium">{formatDate(bookingData.startDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Return</span>
              <span className="text-foreground font-medium">{formatDate(bookingData.endDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Travelers</span>
              <span className="text-foreground font-medium">{bookingData.travelers} Adults</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rooms</span>
              <span className="text-foreground font-medium">{bookingData.rooms} Room{bookingData.rooms > 1 ? 's' : ''}</span>
            </div>
            {bookingData.specialRequests && (
              <div className="pt-2 border-t border-border">
                <span className="text-muted-foreground block mb-1">Special Requests</span>
                <span className="text-foreground text-sm">{bookingData.specialRequests}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Payment Method */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Method
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Provider</span>
              <span className="text-foreground font-medium">{providerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Plan</span>
              <span className="text-foreground font-medium">12 Monthly Installments</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Payment</span>
              <span className="text-foreground font-medium">AED {monthlyPayment}</span>
            </div>
          </div>
        </Card>

        {/* Price Breakdown */}
        <Card className="p-6 bg-primary/5 border-primary/20">
          <h3 className="text-lg font-semibold text-foreground mb-4">Price Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Package Total</span>
              <span className="text-foreground">AED {packagePrice.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Processing Fee</span>
              <span className="text-foreground">Included</span>
            </div>
            <div className="border-t border-border pt-3">
              <div className="flex justify-between text-lg font-semibold">
                <span className="text-foreground">Total Amount</span>
                <span className="text-primary">AED {packagePrice.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Terms and Conditions */}
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">
              By confirming this booking, you agree to our Terms and Conditions and the selected BNPL provider's terms.
            </p>
            <p>
              You will be redirected to {providerName} to complete your payment setup and approval process.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button 
          onClick={onConfirm}
          size="lg"
          className="px-8 py-3"
        >
          Confirm & Pay with {providerName}
        </Button>
      </div>
    </div>
  );
}